/**
 * The quiet line under an answer the output cap paused, with its Continue
 * control — ONE component for every chat that can show a paused answer (the
 * regular chat through MessageItem, which the Concierge and the Guide also
 * use, and the Agent of Agents panel), so it looks and sits the same in all.
 *
 * Continue comes FIRST, at the message's start edge: its place never depends
 * on how long the note is in the current language or how wide the panel is —
 * the note follows it and is the part that may wrap. With no `onContinue`
 * (not the last message, a stream running, a reader who may not write, an
 * assistant without Continue) only the note shows.
 *
 * The click is the panel's; this only shows it as taken at once (disabled)
 * until the panel's attempt settles, however it ends, so a failed Continue
 * can be tried again (`runContinueAttempt`) — the panels' own one-at-a-time
 * guards still turn a second click away.
 */
import { useId, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { runContinueAttempt } from '../utils/continueAttempt';
import './PausedAnswer.css';

/** Continue grows the answer below — a down arrow needs no mirroring in RTL. */
const ContinueIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M12 5v14" />
    <path d="m19 12-7 7-7-7" />
  </svg>
);

interface PausedAnswerProps {
  /** Continue this answer, settling when the attempt ends; absent where it may not be continued. */
  onContinue?: () => Promise<void>;
  /** The UI language's direction: the line is UI text, not the answer's. */
  dir: 'ltr' | 'rtl';
  /** The model's connection failed mid-answer (the output cap did not stop it):
   *  the note says so, Continue is the same. */
  interrupted?: boolean;
  /** Test ids of the line and of its button (each chat keeps its own). */
  testId: string;
  buttonTestId: string;
}

export function PausedAnswer({ onContinue, dir, interrupted, testId, buttonTestId }: PausedAnswerProps) {
  const { t } = useLanguage();
  const noteId = useId();
  const [pending, setPending] = useState(false);

  return (
    <div className="paused-answer" dir={dir} data-testid={testId}>
      {onContinue && (
        <button
          type="button"
          className="paused-answer__continue"
          onClick={() => { void runContinueAttempt(onContinue, setPending); }}
          disabled={pending}
          aria-describedby={noteId}
          data-testid={buttonTestId}
        >
          <ContinueIcon />
          <span>{t('chat.continueAnswer')}</span>
        </button>
      )}
      <span id={noteId} className="paused-answer__note">{t(interrupted ? 'chat.answerInterrupted' : 'chat.answerPaused')}</span>
    </div>
  );
}

export default PausedAnswer;
